import { CardFragment, Color, FragmentItem, TagOption } from '@coast/schemas';
import { isEmpty } from 'lodash';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import slug from 'slug';
import { generateTagColor } from '@shared/components/blocks/lib/tags/utils';
import { createCtx } from '@shared/util/context';
import * as uuid from '@shared/util/uuid';

export interface TagGroup {
  options: TagOption[];
  available: boolean;
}

export type TagGroups = Record<string, TagGroup>;

interface TagOptionsContextValue {
  availableTagGroups: TagGroups;
  createNewGroup(): string;
  addOption(groupId: string, optionLabel: string): void;
  removeOption(groupId: string, optionSlug: string): void;
  cleanupGroups(): void;
}

const [useTagOptionGroups, TagOptionsProvider] = createCtx<TagOptionsContextValue>();

export { useTagOptionGroups, TagOptionsProvider };

interface Props {
  template?: CardFragment;
  children: ReactNode;
}

function getNewGroupsFromTemplate(currentGroups: TagGroups, template?: CardFragment) {
  const newGroups: TagGroups = {};
  const isTagsItem = (item: FragmentItem) => item.fieldType === 'questionSingleSelect';

  template?.items.filter(isTagsItem).forEach(({ settings }) => {
    if (!settings?.options) {
      return;
    }
    const optionsKey = settings.options.map(({ value }) => value).join('');
    if (!currentGroups[optionsKey] && !newGroups[optionsKey]) {
      newGroups[optionsKey] = {
        available: true,
        options: settings.options,
      };
    }
  });

  return newGroups;
}

export function TagOptionsContextProvider({ template, children }: Props) {
  const [groups, setGroups] = useState<TagGroups>({
    ...defaultTagGroups,
    ...getNewGroupsFromTemplate(defaultTagGroups, template),
  });

  useEffect(() => {
    const newGroups = getNewGroupsFromTemplate(groups, template);
    if (Object.keys(newGroups).length) {
      setGroups({ ...groups, ...newGroups });
    }
  }, [groups, template]);

  const createNewGroup = useCallback(() => {
    const groupId = uuid.v4();
    setGroups({
      ...groups,
      [groupId]: {
        options: [],
        available: true,
      },
    });

    return groupId;
  }, [groups]);

  const addOption = useCallback(
    (groupId: string, optionLabel: string) => {
      const oldGroup = groups[groupId];
      if (!oldGroup) {
        console.error('Tag group doesnt exist');
        return;
      }

      const newOptions: TagOption[] = [
        ...oldGroup.options,
        {
          label: optionLabel,
          name: '',
          value: slug(optionLabel),
          theme: { backgroundColor: generateTagColor(oldGroup.options.length) },
        },
      ];

      setGroups({
        ...groups,
        [groupId]: {
          ...oldGroup,
          options: newOptions,
        },
      });
    },
    [groups],
  );

  const cleanupGroups = useCallback(() => {
    setGroups(
      Object.entries(groups).reduce((memo: TagGroups, [key, group]) => {
        if (isEmpty(group.options)) {
          return memo;
        } else {
          return { ...memo, [key]: group };
        }
      }, {}),
    );
  }, [groups]);

  const removeOption = useCallback(
    (groupId: string, optionSlug: string) => {
      const oldGroup = groups[groupId];
      if (!oldGroup) {
        console.error('Tag group doesnt exist');
        return;
      }

      const newOptions: TagOption[] = oldGroup.options.filter(({ value }) => value !== optionSlug);

      setGroups({
        ...groups,
        [groupId]: {
          ...oldGroup,
          options: newOptions,
        },
      });
    },
    [groups],
  );

  const contextValue = {
    createNewGroup,
    availableTagGroups: groups,
    addOption,
    cleanupGroups,
    removeOption,
  };

  return <TagOptionsProvider value={contextValue}>{children}</TagOptionsProvider>;
}

export const defaultTagGroups: TagGroups = {
  yesnona: {
    options: [
      {
        value: 'yes',
        label: 'Yes',
        name: '',
        theme: {
          backgroundColor: Color.tagCoastGreen,
        },
      },
      {
        value: 'no',
        label: 'No',
        name: '',
        theme: {
          backgroundColor: Color.tagPink,
        },
      },
      {
        value: 'na',
        label: 'N/A',
        name: '',
        theme: {
          backgroundColor: Color.tagGray,
        },
      },
    ],
    available: true,
  },
  goodfairpoorna: {
    available: true,
    options: [
      {
        value: 'good',
        label: 'Good',
        name: '',
        theme: {
          backgroundColor: Color.tagCoastGreen,
        },
      },
      {
        value: 'fair',
        label: 'Fair',
        name: '',
        theme: {
          backgroundColor: Color.tagYellow,
        },
      },
      {
        value: 'poor',
        label: 'Poor',
        name: '',
        theme: {
          backgroundColor: Color.tagPink,
        },
      },
      {
        value: 'na',
        label: 'N/A',
        name: '',
        theme: {
          backgroundColor: Color.tagGray,
        },
      },
    ],
  },
  'safeat-riskna': {
    available: true,
    options: [
      {
        value: 'safe',
        label: 'Safe',
        name: '',
        theme: {
          backgroundColor: Color.tagCoastGreen,
        },
      },
      {
        value: 'at-risk',
        label: 'At Risk',
        name: '',
        theme: {
          backgroundColor: Color.tagPink,
        },
      },
      {
        value: 'na',
        label: 'N/A',
        name: '',
        theme: {
          backgroundColor: Color.tagGray,
        },
      },
    ],
  },
  passfailna: {
    available: true,
    options: [
      {
        value: 'pass',
        label: 'Pass',
        name: '',
        theme: {
          backgroundColor: Color.tagCoastGreen,
        },
      },
      {
        value: 'fail',
        label: 'Fail',
        name: '',
        theme: {
          backgroundColor: Color.tagPink,
        },
      },
      {
        value: 'na',
        label: 'N/A',
        name: '',
        theme: {
          backgroundColor: Color.tagGray,
        },
      },
    ],
  },
  'compliantnon-compliantna': {
    available: true,
    options: [
      {
        value: 'compliant',
        label: 'Compliant',
        name: '',
        theme: {
          backgroundColor: Color.tagCoastGreen,
        },
      },
      {
        value: 'non-compliant',
        label: 'Non-Compliant',
        name: '',
        theme: {
          backgroundColor: Color.tagPink,
        },
      },
      {
        value: 'na',
        label: 'N/A',
        name: '',
        theme: {
          backgroundColor: Color.tagGray,
        },
      },
    ],
  },
};

export const defaultTagGroupsForChecklists: TagGroups = {
  completeincomplete: {
    available: true,
    options: [
      {
        value: 'complete',
        label: 'Complete',
        name: '',
        theme: {
          backgroundColor: Color.tagCoastGreen,
        },
      },
      {
        value: 'incomplete',
        label: 'Incomplete',
        name: '',
        theme: {
          backgroundColor: Color.tagPink,
        },
      },
    ],
  },
  yesno: {
    options: [
      {
        value: 'yes',
        label: 'Yes',
        name: '',
        theme: {
          backgroundColor: Color.tagCoastGreen,
        },
      },
      {
        value: 'no',
        label: 'No',
        name: '',
        theme: {
          backgroundColor: Color.tagPink,
        },
      },
    ],
    available: true,
  },
  'safeat-risk': {
    available: true,
    options: [
      {
        value: 'safe',
        label: 'Safe',
        name: '',
        theme: {
          backgroundColor: Color.tagCoastGreen,
        },
      },
      {
        value: 'at-risk',
        label: 'At Risk',
        name: '',
        theme: {
          backgroundColor: Color.tagPink,
        },
      },
    ],
  },
  passfail: {
    available: true,
    options: [
      {
        value: 'pass',
        label: 'Pass',
        name: '',
        theme: {
          backgroundColor: Color.tagCoastGreen,
        },
      },
      {
        value: 'fail',
        label: 'Fail',
        name: '',
        theme: {
          backgroundColor: Color.tagPink,
        },
      },
    ],
  },
  'compliantnon-compliant': {
    available: true,
    options: [
      {
        value: 'compliant',
        label: 'Compliant',
        name: '',
        theme: {
          backgroundColor: Color.tagCoastGreen,
        },
      },
      {
        value: 'non-compliant',
        label: 'Non-Compliant',
        name: '',
        theme: {
          backgroundColor: Color.tagPink,
        },
      },
    ],
  },
};
